import { ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { PropertyService } from '../services/property.service';
import { TranslationService } from '../services/translation.service';
import { HospitalityDigitalKeyAccessType } from '../models/hospitality-digital-key.model';
import { MapCoordinates, normalizeMapUrl, NormalizedMapUrl } from '../utils/map-url.utils';
import { toValidMapAddress } from '../utils/validators/map-address.validator';
import { toValidLatitude, toValidLongitude } from '../utils/validators/map-coordinate.validator';
import {HospitalityLink} from '../property-list/property-list.models';

export interface HospitalityMapMarker {
  lat: number;
  lng: number;
  label?: string;
  url?: string;
  kind?: 'property' | 'highlight' | 'center';
}

export interface HospitalityLinkDocumentViewModel {
  fileName?: string;
  fileType?: string;
  url?: string;
  previewUrl?: string;
  thumbnailUrl?: string;
}

export interface HospitalityLinkViewModel {
  label?: string;
  url?: string;
  source?: 'property' | 'company';
  mapLocation?: NormalizedMapUrl;
  coordinates?: MapCoordinates;
  document?: HospitalityLinkDocumentViewModel;
  address?: string;
}

export interface HospitalityDigitalKeyViewModel {
  type: HospitalityDigitalKeyAccessType;
  typeLabel: string;
  primaryLabelKey: string;
  primaryValue?: string;
  primaryLink?: string;
  notesLabelKey?: string;
  notes?: string;
  secondaryLinkLabelKey?: string;
  secondaryLink?: string;
}

export interface HospitalityChecklistViewModel {
  title?: string;
  items: string[];
}

export interface HospitalityGuideViewModel {
  appliance?: string;
  summary?: string;
  steps: string[];
  resources: HospitalityLinkViewModel[];
}

export interface HospitalityHouseRuleViewModel {
  title?: string;
  summary?: string;
  details?: string;
  links?: HospitalityLinkViewModel[];
}

export interface HospitalityTimelineViewModel {
  type: 'check-in' | 'check-out';
  titleKey: string;
  from?: string;
  until?: string;
  instructions?: string;
  location?: string;
  contact?: string;
  links: HospitalityLinkViewModel[];
  digitalKeys: HospitalityDigitalKeyViewModel[];
  checklist?: HospitalityChecklistViewModel;
}

export interface HospitalityInfoSectionViewModel {
  title?: string;
  body?: string;
  links: HospitalityLinkViewModel[];
  key?: string;
  source?: 'property' | 'company';
}

export interface HospitalityFeedbackViewModel {
  instructions?: string;
  email?: string;
  phone?: string;
  formUrl?: string;
  source?: 'property' | 'company';
}

export interface HospitalityLocalTipsViewModel {
  intro?: string;
  mapEmbedUrl?: SafeResourceUrl;
  mapUrl?: string;
  mapCoordinates?: MapCoordinates | null;
  highlights: HospitalityLinkViewModel[];
  mapMarkers: HospitalityMapMarker[];
  mapCenter?: MapCoordinates;
  staticMapUrl?: string;
}

export interface HospitalityRatingSummary {
  count: number;
  averages: {
    cleanliness: number;
    comfort: number;
    communication: number;
    overall: number;
  };
  overallAverage: number;
  lastSubmittedAt: string | null;
}

export type HospitalityRatingKey = 'cleanliness' | 'comfort' | 'communication' | 'overall';

export abstract class LandingPageHospitalityMixin {
  protected abstract companyId: string | undefined;
  protected abstract propertyId: string | undefined;
  protected abstract company: any;
  protected abstract property: any;
  protected abstract appMode: 'emergency' | 'hospitality';
  protected abstract selectedLanguage: string;
  protected abstract track(type: string, metadata?: any): void;

  hospitalityWelcomeMessage?: string;
  hospitalityGuestNotes?: string;
  hospitalityHostContact?: { name?: string; phone?: string; email?: string; whatsapp?: string; supportHours?: string };
  hospitalityWifi?: { networkName?: string; password?: string; instructions?: string; supportContact?: string };
  hospitalityTimeline: HospitalityTimelineViewModel[] = [];
  hospitalityHouseRules?: HospitalityHouseRuleViewModel[];
  hospitalityGuides: HospitalityGuideViewModel[] = [];
  hospitalityLocalTips?: HospitalityLocalTipsViewModel;
  hospitalityInfoSections: HospitalityInfoSectionViewModel[] = [];
  hospitalityFeedback?: HospitalityFeedbackViewModel;
  hospitalityRatingSummary?: HospitalityRatingSummary;
  showHospitalityRatingCard = true;
  hospitalityRatingScores: Record<HospitalityRatingKey, number> = {
    cleanliness: 0,
    comfort: 0,
    communication: 0,
    overall: 0,
  };
  hospitalityRatingSubmitting = false;
  hospitalityRatingSubmitted = false;
  hospitalityRatingError?: string;
  hospitalityRatingSuccess?: string;
  hospitalityRatingSubmitAttempted = false;
  readonly hospitalityRatingCategories: Array<{ key: HospitalityRatingKey; labelKey: string }> = [
    { key: 'cleanliness', labelKey: 'LANDING.HOSPITALITY.RATING_CLEANLINESS' },
    { key: 'comfort', labelKey: 'LANDING.HOSPITALITY.RATING_COMFORT' },
    { key: 'communication', labelKey: 'LANDING.HOSPITALITY.RATING_COMMUNICATION' },
    { key: 'overall', labelKey: 'LANDING.HOSPITALITY.RATING_OVERALL' },
  ];
  showHospitalityEmptyState = false;

  protected hospitalityDetailsRaw: any;
  protected hospitalityLandingRaw: any;
  protected ratingSubmissionSub?: Subscription;

  showAllRules = false;
  showAllChecklist = false;

  protected constructor(
    protected translate: TranslateService,
    protected translationService: TranslationService,
    protected propertyService: PropertyService,
    protected cdr: ChangeDetectorRef,
    protected sanitizer: DomSanitizer,
  ) {}

  protected buildHospitalityViewModels(): void {
    const details = this.hospitalityDetailsRaw || {};
    const landing = this.hospitalityLandingRaw || {};

    const welcomeMessage = this.sanitizeString(details?.welcomeMessage) || this.sanitizeString(landing?.welcomeMessage);
    this.translateHospitalityField(welcomeMessage, value => {
      this.hospitalityWelcomeMessage = value;
    });

    const guestNotes = this.sanitizeString(details?.guestNotes) || this.sanitizeString(landing?.guestNotes);
    this.translateHospitalityField(guestNotes, value => {
      this.hospitalityGuestNotes = value;
    });

    const hostContact = details?.hostContact && typeof details.hostContact === 'object' ? details.hostContact : landing?.hostContact;
    if (hostContact && typeof hostContact === 'object') {
      const normalized = {
        name: this.sanitizeString(hostContact.name),
        phone: this.sanitizeString(hostContact.phone),
        email: this.sanitizeString(hostContact.email),
        whatsapp: this.sanitizeString(hostContact.whatsapp),
        supportHours: this.sanitizeString(hostContact.supportHours),
      };
      if (normalized.name || normalized.phone || normalized.email || normalized.whatsapp || normalized.supportHours) {
        this.hospitalityHostContact = normalized;
        if (normalized.supportHours) {
          const initialSupportHours = normalized.supportHours;
          this.translateHospitalityField(hostContact.supportHours, value => {
            if (this.hospitalityHostContact && this.hospitalityHostContact.supportHours === initialSupportHours) {
              this.hospitalityHostContact.supportHours = value;
            }
          });
        }
        if (normalized.name) {
          const initialName = normalized.name;
          this.translateHospitalityField(hostContact.name, value => {
            if (this.hospitalityHostContact && this.hospitalityHostContact.name === initialName) {
              this.hospitalityHostContact.name = value;
            }
          });
        }
      } else {
        this.hospitalityHostContact = undefined;
      }
    } else {
      this.hospitalityHostContact = undefined;
    }

    const wifi = details?.wifi && typeof details.wifi === 'object' ? details.wifi : landing?.wifi;
    if (wifi && typeof wifi === 'object') {
      const networkName = this.sanitizeString(wifi.networkName);
      const password = this.sanitizeString(wifi.password);
      const instructions = this.sanitizeString(wifi.instructions);
      const supportContact = this.sanitizeString(wifi.supportContact);
      if (networkName || password || instructions || supportContact) {
        const wifiModel = { networkName, password, instructions, supportContact };
        this.hospitalityWifi = wifiModel;
        this.translateHospitalityField(wifi.instructions, value => {
          if (this.hospitalityWifi === wifiModel && this.hospitalityWifi.instructions === instructions) {
            this.hospitalityWifi.instructions = value;
          }
        });
        if (supportContact) {
          this.translateHospitalityField(wifi.supportContact, value => {
            if (this.hospitalityWifi === wifiModel && this.hospitalityWifi.supportContact === supportContact) {
              this.hospitalityWifi.supportContact = value;
            }
          });
        }
      } else {
        this.hospitalityWifi = undefined;
      }
    } else if (landing?.wifi && typeof landing.wifi === 'object') {
      const defaultWifi = landing.wifi;
      const defaultWifiNetwork = this.sanitizeString(defaultWifi?.networkName);
      const defaultWifiPassword = this.sanitizeString(defaultWifi?.password);
      const defaultWifiInstructions = this.sanitizeString(defaultWifi?.instructions);
      if (defaultWifiNetwork || defaultWifiPassword || defaultWifiInstructions) {
        const fallbackWifi = {
          networkName: defaultWifiNetwork,
          password: defaultWifiPassword,
          instructions: defaultWifiInstructions,
          supportContact: undefined,
        };
        this.hospitalityWifi = fallbackWifi;
        this.translateHospitalityField(defaultWifi?.instructions, value => {
          if (this.hospitalityWifi === fallbackWifi && this.hospitalityWifi.instructions === defaultWifiInstructions) {
            this.hospitalityWifi.instructions = value;
          }
        });
      } else {
        this.hospitalityWifi = undefined;
      }
    }

    const timeline: HospitalityTimelineViewModel[] = [];
    const checkInDetails = details?.checkIn;
    if (this.isHospitalitySectionPayload(checkInDetails)) {
      const companyEntries = this.filterAndSortEntryGroups(
        Array.isArray(checkInDetails?.companyEntries) ? checkInDetails.companyEntries : [],
      );
      companyEntries.forEach(entry => {
        const view = this.mapCheckInCompanyEntryToTimeline(entry);
        if (view) {
          timeline.push(view);
        }
      });
      const ownedEntries = this.filterAndSortEntryGroups(
        Array.isArray(checkInDetails?.ownedEntries) ? checkInDetails.ownedEntries : [],
      );
      ownedEntries.forEach(entry => {
        const view = this.mapCheckInOwnedEntryToTimeline(entry);
        if (view) {
          timeline.push(view);
        }
      });
    } else if (checkInDetails && typeof checkInDetails === 'object') {
      const legacyView = this.mapLegacyCheckInToTimeline(checkInDetails);
      if (legacyView) {
        timeline.push(legacyView);
      }
    }

    const checkOutDetails = details?.checkOut;
    if (this.isHospitalitySectionPayload(checkOutDetails)) {
      const companyEntries = this.filterAndSortEntryGroups(
        Array.isArray(checkOutDetails?.companyEntries) ? checkOutDetails.companyEntries : [],
      );
      companyEntries.forEach(entry => {
        const view = this.mapCheckOutCompanyEntryToTimeline(entry);
        if (view) {
          timeline.push(view);
        }
      });
      const ownedEntries = this.filterAndSortEntryGroups(
        Array.isArray(checkOutDetails?.ownedEntries) ? checkOutDetails.ownedEntries : [],
      );
      ownedEntries.forEach(entry => {
        const view = this.mapCheckOutOwnedEntryToTimeline(entry);
        if (view) {
          timeline.push(view);
        }
      });
    } else if (checkOutDetails && typeof checkOutDetails === 'object') {
      const legacyView = this.mapLegacyCheckOutToTimeline(checkOutDetails);
      if (legacyView) {
        timeline.push(legacyView);
      }
    }
    this.hospitalityTimeline = timeline;

    if (details?.houseRules && typeof details.houseRules === 'object') {
      const entries: HospitalityHouseRuleViewModel[] = [];
      const houseRules: any = details.houseRules;

      if (Array.isArray(houseRules.ownedEntries) || Array.isArray(houseRules.companyEntries)) {
        const companyViews: HospitalityHouseRuleViewModel[] = [];
        const propertyViews: HospitalityHouseRuleViewModel[] = [];

        const companyEntries = this.filterAndSortEntryGroups(
          Array.isArray(houseRules.companyEntries) ? houseRules.companyEntries : [],
        );
        companyEntries.forEach((entry: any) => {
          const content = entry?.content && typeof entry.content === 'object' ? entry.content : {};
          const overrides = entry?.overrides && typeof entry.overrides === 'object' ? entry.overrides : {};
          const overrideTitle = this.sanitizeString((overrides as any)?.title);
          const contentTitle = this.sanitizeString((content as any)?.title);
          const overrideSummary = this.sanitizeString((overrides as any)?.summary);
          const contentSummary = this.sanitizeString((content as any)?.summary);
          const overrideDetails = this.sanitizeString((overrides as any)?.details);
          const contentDetails = this.sanitizeString((content as any)?.details);
          const overrideRule = this.sanitizeString((overrides as any)?.rule);
          const contentRule = this.sanitizeString((content as any)?.rule);
          const resolvedRule = overrideRule || contentRule || undefined;
          const resolvedTitle =
            overrideTitle ||
            contentTitle ||
            overrideSummary ||
            contentSummary ||
            overrideDetails ||
            contentDetails ||
            resolvedRule;
          const resolvedSummary =
            overrideSummary ||
            contentSummary ||
            overrideDetails ||
            contentDetails ||
            resolvedTitle ||
            resolvedRule;
          const resolvedDetails =
            overrideDetails ||
            contentDetails ||
            resolvedSummary ||
            resolvedTitle ||
            resolvedRule;
          const overrideLinks = this.normalizeHospitalityLinks((overrides as any)?.links, 'company');
          const contentLinks = this.normalizeHospitalityLinks((content as any)?.links, 'company');
          const links = [...overrideLinks, ...contentLinks];
          if (resolvedTitle || resolvedSummary || resolvedDetails || links.length) {
            const view: HospitalityHouseRuleViewModel = {
              title: resolvedTitle || resolvedSummary || resolvedDetails,
              summary: resolvedSummary || resolvedTitle || resolvedDetails,
              details: resolvedDetails || resolvedSummary || resolvedTitle,
            };
            if (links.length) {
              view.links = links;
            }
            const initialTitle = view.title;
            if (initialTitle) {
              this.translateHospitalityField(initialTitle, value => {
                if (view.title === initialTitle) {
                  view.title = value;
                }
              });
            }
            const initialSummary = view.summary;
            if (initialSummary) {
              this.translateHospitalityField(initialSummary, value => {
                if (view.summary === initialSummary) {
                  view.summary = value;
                }
              });
            }
            const initialDetails = view.details;
            if (initialDetails) {
              this.translateHospitalityField(initialDetails, value => {
                if (view.details === initialDetails) {
                  view.details = value;
                }
              });
            }
            companyViews.push(view);
          }
        });

        const ownedEntries = this.filterAndSortEntryGroups(
          Array.isArray(houseRules.ownedEntries) ? houseRules.ownedEntries : [],
        );
        ownedEntries.forEach((entry: any) => {
          const title = this.sanitizeString(entry?.title);
          const summary = this.sanitizeString(entry?.summary);
          const detailsText = this.sanitizeString(entry?.details);
          const rule = this.sanitizeString(entry?.rule);
          const links = this.normalizeHospitalityLinks(entry?.links);
          if (title || summary || detailsText || rule || links.length) {
            const view: HospitalityHouseRuleViewModel = {
              title: title || summary || detailsText || rule,
              summary: summary || title || detailsText || rule,
              details: detailsText || summary || title || rule,
            };
            if (links.length) {
              view.links = links;
            }
            const initialTitle = view.title;
            if (initialTitle) {
              this.translateHospitalityField(initialTitle, value => {
                if (view.title === initialTitle) {
                  view.title = value;
                }
              });
            }
            const initialSummary = view.summary;
            if (initialSummary) {
              this.translateHospitalityField(initialSummary, value => {
                if (view.summary === initialSummary) {
                  view.summary = value;
                }
              });
            }
            const initialDetails = view.details;
            if (initialDetails) {
              this.translateHospitalityField(initialDetails, value => {
                if (view.details === initialDetails) {
                  view.details = value;
                }
              });
            }
            propertyViews.push(view);
          }
        });

        entries.push(...companyViews, ...propertyViews);
      } else {
        const summary = this.sanitizeString(houseRules.summary);
        const rules = this.normalizeStringArray(houseRules.rules);
        const links = this.normalizeHospitalityLinks(houseRules.links);
        if (summary) {
          const view: HospitalityHouseRuleViewModel = { title: summary, summary, details: summary };
          if (links.length) {
            view.links = links;
          }
          this.translateHospitalityField(summary, value => {
            if (view.title === summary) {
              view.title = value;
            }
            if (view.summary === summary) {
              view.summary = value;
            }
            if (view.details === summary) {
              view.details = value;
            }
          });
          entries.push(view);
        }
        rules.forEach((rule: string) => {
          const text = this.sanitizeString(rule);
          if (text) {
            const view: HospitalityHouseRuleViewModel = { title: text, summary: text, details: text };
            this.translateHospitalityField(text, value => {
              if (view.title === text) {
                view.title = value;
              }
              if (view.summary === text) {
                view.summary = value;
              }
              if (view.details === text) {
                view.details = value;
              }
            });
            entries.push(view);
          }
        });
        if (!summary && !rules.length && links.length) {
          entries.push({ links });
        }
      }

      this.hospitalityHouseRules = entries.length ? entries : undefined;
    } else {
      this.hospitalityHouseRules = undefined;
    }

    const applianceGuides = details?.applianceGuides;
    const companyGuides: HospitalityGuideViewModel[] = [];
    const propertyGuides: HospitalityGuideViewModel[] = [];
    const pushGuide = (
      target: 'company' | 'property',
      appliance?: string | null,
      summary?: string | null,
      rawSteps?: any,
      rawResources?: any,
      fallbackDetails?: string | null,
      rawLinks?: any,
    ) => {
      const applianceValue = this.sanitizeString(appliance);
      const summaryValue = this.sanitizeString(summary);
      const steps = this.normalizeGuideSteps(rawSteps, fallbackDetails);
      const normalizedResources = [
        ...this.normalizeHospitalityLinks(rawResources, target),
        ...this.normalizeHospitalityLinks(rawLinks, target),
      ];
      const seenResources = new Set<string>();
      const resources = normalizedResources.filter(resource => {
        const key = `${resource.url || ''}|${resource.label || ''}|${resource.source || ''}`;
        if (seenResources.has(key)) {
          return false;
        }
        seenResources.add(key);
        return true;
      });
      if ((applianceValue && applianceValue.trim().length > 0) || (summaryValue && summaryValue.trim().length > 0) || steps.length || resources.length) {
        const guide: HospitalityGuideViewModel = {
          appliance: applianceValue || undefined,
          summary: summaryValue || undefined,
          steps,
          resources,
        };
        if (guide.appliance) {
          const initialAppliance = guide.appliance;
          this.translateHospitalityField(applianceValue, value => {
            if (guide.appliance === initialAppliance) {
              guide.appliance = value;
            }
          });
        }
        if (guide.summary) {
          const initialSummary = guide.summary;
          this.translateHospitalityField(summaryValue, value => {
            if (guide.summary === initialSummary) {
              guide.summary = value;
            }
          });
        }
        guide.steps.forEach((step, index) => {
          const initialStep = step;
          this.translateHospitalityField(step, value => {
            if (guide.steps[index] === initialStep) {
              guide.steps[index] = value || initialStep;
            }
          });
        });
        if (target === 'company') {
          companyGuides.push(guide);
        } else {
          propertyGuides.push(guide);
        }
      }
    };

    if (applianceGuides && typeof applianceGuides === 'object' && !Array.isArray(applianceGuides)) {
      const companyEntries = this.filterAndSortEntryGroups(
        Array.isArray(applianceGuides.companyEntries) ? applianceGuides.companyEntries : [],
      );
      companyEntries.forEach((entry: any) => {
        const content = entry?.content && typeof entry.content === 'object' ? entry.content : {};
        const overrides = entry?.overrides && typeof entry.overrides === 'object' ? entry.overrides : {};
        const appliance = this.sanitizeString((overrides as any)?.appliance)
          || this.sanitizeString((content as any)?.appliance)
          || this.sanitizeString((overrides as any)?.title)
          || this.sanitizeString((content as any)?.title);
        const summary = this.sanitizeString((overrides as any)?.summary)
          || this.sanitizeString((content as any)?.summary)
          || appliance;
        const stepsSource = (overrides as any)?.steps ?? (content as any)?.steps;
        const details = this.sanitizeString((overrides as any)?.details)
          || this.sanitizeString((content as any)?.details)
          || null;
        const rawResources = [
          ...((Array.isArray((overrides as any)?.resources) ? (overrides as any)?.resources : []) as any[]),
          ...((Array.isArray((content as any)?.resources) ? (content as any)?.resources : []) as any[]),
        ];
        const rawLinks = [
          ...((Array.isArray((overrides as any)?.links) ? (overrides as any)?.links : []) as any[]),
          ...((Array.isArray((content as any)?.links) ? (content as any)?.links : []) as any[]),
        ];
        pushGuide('company', appliance, summary, stepsSource, rawResources, details, rawLinks);
      });

      const ownedEntries = this.filterAndSortEntryGroups(
        Array.isArray(applianceGuides.ownedEntries) ? applianceGuides.ownedEntries : [],
      );
      ownedEntries.forEach((entry: any) => {
        const appliance = this.sanitizeString(entry?.appliance);
        const summary = this.sanitizeString(entry?.summary);
        const details = this.sanitizeString(entry?.details) || null;
        const rawLinks = Array.isArray(entry?.links) ? entry?.links : undefined;
        pushGuide('property', appliance, summary, entry?.steps, entry?.resources, details, rawLinks);
      });
    } else if (Array.isArray(applianceGuides)) {
      applianceGuides.forEach((entry: any) => {
        pushGuide('property', entry?.appliance, entry?.summary, entry?.steps, entry?.resources, entry?.details, entry?.links);
      });
    }

    this.hospitalityGuides = [...companyGuides, ...propertyGuides];

    const localTipsRaw = details?.localTips && typeof details.localTips === 'object' ? details.localTips : landing?.localTips;
    if (localTipsRaw && typeof localTipsRaw === 'object') {
      const intro = this.sanitizeString(localTipsRaw?.intro);
      const rawEmbedUrl = this.sanitizeString(localTipsRaw?.mapEmbedUrl);
      const rawMapLink = this.sanitizeString(localTipsRaw?.mapUrl);
      const coordinates = this.resolveMapCoordinates(localTipsRaw?.mapCoordinates);
      const highlights = this.normalizeHospitalityLinks(localTipsRaw.highlights);
      const localTipsView: HospitalityLocalTipsViewModel = {
        intro,
        mapEmbedUrl: rawEmbedUrl
          ? this.sanitizer.bypassSecurityTrustResourceUrl(rawEmbedUrl)
          : undefined,
        mapUrl: rawMapLink || undefined,
        mapCoordinates: coordinates || null,
        highlights,
        mapMarkers: [],
      };
      if (localTipsView.intro) {
        const initialIntro = localTipsView.intro;
        this.translateHospitalityField(localTipsRaw?.intro, value => {
          if (localTipsView.intro === initialIntro) {
            localTipsView.intro = value;
          }
        });
      }
      const propertyCoordinates = this.resolveMapCoordinates(this.property?.coordinates);
      const propertyLabel =
        this.sanitizeString(this.property?.address) || this.sanitizeString(this.property?.name);
      this.setHospitalityLocalTips(localTipsView, propertyCoordinates || null, propertyLabel || undefined);
    } else {
      this.hospitalityLocalTips = undefined;
    }

    const infoSectionSources: any[] = [];
    const additionalInfoDetails = details?.additionalInfo;
    if (this.isHospitalitySectionPayload(additionalInfoDetails)) {
      const companyAdditional = this.filterAndSortEntryGroups(
        Array.isArray(additionalInfoDetails.companyEntries) ? additionalInfoDetails.companyEntries : [],
      );
      companyAdditional.forEach((entry: any, index: number) => {
        const content = entry?.content && typeof entry.content === 'object' ? entry.content : {};
        const overrides = entry?.overrides && typeof entry.overrides === 'object' ? entry.overrides : {};
        const title = (overrides as any)?.title ?? (content as any)?.title;
        const body = (overrides as any)?.body ?? (content as any)?.body;
        const overrideLinks = Array.isArray((overrides as any)?.links) ? (overrides as any).links : [];
        const contentLinks = Array.isArray((content as any)?.links) ? (content as any).links : [];
        infoSectionSources.push({
          title,
          body,
          links: [...overrideLinks, ...contentLinks],
          key:
            (overrides as any)?.key ||
            (content as any)?.key ||
            (content as any)?.id ||
            entry?.entryId ||
            entry?._id ||
            `company-additional-${index}`,
          source: 'company',
        });
      });

      const propertyAdditional = this.filterAndSortEntryGroups(
        Array.isArray(additionalInfoDetails.ownedEntries) ? additionalInfoDetails.ownedEntries : [],
      );
      propertyAdditional.forEach((entry: any, index: number) => {
        if (!entry || typeof entry !== 'object') {
          return;
        }
        infoSectionSources.push({
          title: entry?.title,
          body: entry?.body,
          links: entry?.links,
          key: entry?.key || entry?._id || `property-additional-${index}`,
          source: 'property',
        });
      });
    } else if (Array.isArray(additionalInfoDetails)) {
      additionalInfoDetails.forEach((entry: any, index: number) => {
        if (!entry || typeof entry !== 'object') {
          return;
        }
        infoSectionSources.push({
          ...entry,
          source: entry?.source === 'company' ? 'company' : 'property',
          key: entry?.key || entry?._id || `property-additional-${index}`,
        });
      });
    }

    let explicitInfoSections: any[] | undefined;
    if (Array.isArray(details?.infoSections) && details.infoSections.length) {
      explicitInfoSections = details.infoSections;
    } else if (Array.isArray(landing?.infoSections) && landing.infoSections.length) {
      explicitInfoSections = landing.infoSections;
    }

    if (Array.isArray(explicitInfoSections)) {
      explicitInfoSections.forEach((section: any, index: number) => {
        if (!section || typeof section !== 'object') {
          return;
        }
        infoSectionSources.push({
          ...section,
          source: section?.source === 'company' ? 'company' : 'property',
          key: section?.key || section?.id || section?._id || `section-${index}`,
        });
      });
    }

    if (infoSectionSources.length) {
      const sections = infoSectionSources
        .map((section: any, index: number) => {
          if (!section || typeof section !== 'object') {
            return null;
          }
          const title = this.sanitizeString(section?.title);
          const body = this.sanitizeString(section?.body);
          const source: 'property' | 'company' = section?.source === 'company' ? 'company' : 'property';
          const links = this.normalizeHospitalityLinks(section?.links, source);
          if (!(title || body || links.length)) {
            return null;
          }
          const view: HospitalityInfoSectionViewModel = {
            title,
            body,
            links,
            key: section?.key || section?.id || section?._id || `section-${index}`,
            source,
          };
          this.translateHospitalityField(section?.title, value => {
            if (view.title === title) {
              view.title = value;
            }
          });
          this.translateHospitalityField(section?.body, value => {
            if (view.body === body) {
              view.body = value;
            }
          });
          return view;
        })
        .filter((section): section is HospitalityInfoSectionViewModel => !!section);

      this.hospitalityInfoSections = sections;
    } else {
      this.hospitalityInfoSections = [];
    }

    const propertyFeedback = this.createHospitalityFeedbackViewModel(details?.feedback, 'property');
    const companyFeedback = this.createHospitalityFeedbackViewModel(landing?.feedback, 'company');
    this.hospitalityFeedback = propertyFeedback || companyFeedback;

    const ratingSummary = this.extractHospitalityRatingSummary(details, landing);
    this.hospitalityRatingSummary = ratingSummary || this.hospitalityRatingSummary;

    const hasSections = !!(
      this.hospitalityWifi ||
      this.hospitalityTimeline.length ||
      this.hospitalityHouseRules ||
      this.hospitalityGuides.length ||
      this.hospitalityLocalTips ||
      this.hospitalityFeedback ||
      this.hospitalityInfoSections.length ||
      this.hospitalityRatingSummary
    );
    this.showHospitalityEmptyState = !(hasSections || this.showHospitalityRatingCard);

    this.cdr.detectChanges();
  }

  protected translateHospitalityField(
    value: string | undefined,
    setter: (translated?: string) => void
  ): void {
    const sanitized = this.sanitizeString(value);
    const targetLang = (this.selectedLanguage || '').toLowerCase();

    // If empty: clear the field and stop
    if (!sanitized) {
      setter(undefined);
      return;
    }

    // Backend texts are stored in German.
    // For German we show the raw text immediately.
    if (!targetLang || targetLang === 'de') {
      setter(sanitized);
      return;
    }

    // For non-German languages:
    // keep whatever is currently visible (previous translation or template fallback)
    // and only update once the translated text arrives.
    const pendingLang = targetLang;

    this.translationService
      .translateText(sanitized, targetLang)
      .pipe(
        map(res => (res?.responseData?.translatedText || sanitized).trim()),
        catchError(() => of(sanitized)),
      )
      .subscribe(translated => {
        // User might have changed language again while this request was in flight.
        if ((this.selectedLanguage || '').toLowerCase() !== pendingLang) {
          return;
        }

        setter(translated);
        this.cdr.markForCheck();
      });
  }

  protected normalizeHospitalityLinks(items: any, source: 'property' | 'company' = 'property'): HospitalityLinkViewModel[] {
    if (!Array.isArray(items)) {
      return [];
    }
    return items
      .map((item: any) => {
        if (!item || typeof item !== 'object') {
          return null;
        }
        const label = typeof item.label === 'string' ? item.label.trim() : undefined;
        const rawUrl = typeof item.url === 'string' ? item.url.trim() : '';
        const address = this.sanitizeString(item.address);
        let url: string | undefined;
        let mapLocation: NormalizedMapUrl | undefined;
        let coordinates = this.resolveMapCoordinates(item.coordinates);
        if (rawUrl) {
          if (/^(mailto:|tel:)/i.test(rawUrl)) {
            url = rawUrl;
          } else {
            const normalizedMap = normalizeMapUrl(rawUrl);
            if (normalizedMap) {
              url = normalizedMap.canonicalUrl;
              mapLocation = normalizedMap;
              if (!coordinates && normalizedMap.coordinates) {
                coordinates = normalizedMap.coordinates;
              }
            } else {
              url = this.normalizeUrl(rawUrl);
            }
          }
        }
        if (!coordinates && item.lat && item.lng) {
          coordinates = this.resolveMapCoordinates({ lat: item.lat, lng: item.lng });
        }
        const document = this.normalizeHospitalityLinkDocument(item.document);
        if (label || url || coordinates || document || address) {
          const link: HospitalityLinkViewModel = { label, url, source };
          if (label) {
            const initialLabel = label;
            this.translateHospitalityField(label, value => {
              if (link.label === initialLabel) {
                link.label = value;
              }
            });
          }
          if (address) {
            link.address = address;
          }
          if (mapLocation) {
            link.mapLocation = mapLocation;
          }
          if (coordinates) {
            link.coordinates = coordinates;
          }
          if (document) {
            link.document = document;
          }
          return link;
        }
        return null;
      })
      .filter((item): item is HospitalityLinkViewModel => !!item);
  }

  private normalizeHospitalityLinkDocument(raw: any): HospitalityLinkDocumentViewModel | undefined {
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }
    const fileName = this.sanitizeString(raw.fileName) || this.sanitizeString(raw.name);
    const url = this.sanitizeString(raw.url) || this.sanitizeString(raw.fileUrl);
    const fileType = this.sanitizeString(raw.fileType) || this.sanitizeString(raw.mimeType);
    const previewUrl = this.sanitizeString(raw.previewUrl);
    const thumbnailUrl = this.sanitizeString(raw.thumbnailUrl);
    if (!fileName && !url && !fileType && !previewUrl && !thumbnailUrl) {
      return undefined;
    }
    const document: HospitalityLinkDocumentViewModel = {};
    if (fileName) {
      document.fileName = fileName;
    }
    if (url) {
      document.url = url;
    }
    if (fileType) {
      document.fileType = fileType;
    }
    if (previewUrl) {
      document.previewUrl = previewUrl;
    }
    if (thumbnailUrl) {
      document.thumbnailUrl = thumbnailUrl;
    }
    return Object.keys(document).length ? document : undefined;
  }

  protected normalizeHospitalityDigitalKeys(
    items: any,
    _source: 'property' | 'company' = 'property',
  ): HospitalityDigitalKeyViewModel[] {
    if (!Array.isArray(items)) {
      return [];
    }
    return items
      .map(item => this.normalizeHospitalityDigitalKey(item))
      .filter((key): key is HospitalityDigitalKeyViewModel => !!key);
  }

  private normalizeHospitalityDigitalKey(item: any): HospitalityDigitalKeyViewModel | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    if (typeof (item as any).accessType === 'string') {
      const type = this.normalizeDigitalKeyType((item as any).accessType);
      if (!type) {
        return null;
      }
      const code = this.sanitizeString((item as any).code);
      const notes = this.sanitizeString((item as any).notes);
      const link = this.normalizeDigitalKeyLink((item as any).link);
      return this.buildDigitalKeyViewModel(type, { code, notes, link });
    }

    const label = this.sanitizeString((item as any).label);
    const legacyLink = this.normalizeDigitalKeyLink((item as any).url);

    if (legacyLink) {
      return this.buildDigitalKeyViewModel('app', { link: legacyLink, notes: label });
    }

    if (label) {
      return this.buildDigitalKeyViewModel('other', { notes: label });
    }

    return null;
  }

  private normalizeDigitalKeyType(value: any): HospitalityDigitalKeyAccessType | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const normalized = value.trim().toLowerCase();
    if (normalized === 'pin' || normalized === 'app' || normalized === 'other') {
      return normalized as HospitalityDigitalKeyAccessType;
    }
    return undefined;
  }

  private normalizeDigitalKeyLink(value: any): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const raw = value.trim();
    if (!raw) {
      return undefined;
    }
    if (/^(mailto:|tel:)/i.test(raw)) {
      return raw;
    }
    return this.normalizeUrl(raw);
  }

  private buildDigitalKeyViewModel(
    type: HospitalityDigitalKeyAccessType,
    data: { code?: string; notes?: string; link?: string },
  ): HospitalityDigitalKeyViewModel | null {
    const typeLabel = this.getDigitalKeyTypeLabel(type);
    if (type === 'pin') {
      if (!data.code) {
        return null;
      }
      const view: HospitalityDigitalKeyViewModel = {
        type,
        typeLabel,
        primaryLabelKey: 'LANDING.HOSPITALITY.DIGITAL_KEY_CODE',
        primaryValue: data.code,
      };
      if (data.notes) {
        view.notesLabelKey = 'LANDING.HOSPITALITY.DIGITAL_KEY_NOTES';
        view.notes = data.notes;
        const initialNotes = data.notes;
        this.translateHospitalityField(data.notes, value => {
          if (view.notes === initialNotes) {
            view.notes = value;
          }
        });
      }
      return view;
    }

    if (type === 'app') {
      if (!data.link) {
        return null;
      }
      const view: HospitalityDigitalKeyViewModel = {
        type,
        typeLabel,
        primaryLabelKey: 'LANDING.HOSPITALITY.DIGITAL_KEY_LINK',
        primaryLink: data.link,
      };
      if (data.notes) {
        view.notesLabelKey = 'LANDING.HOSPITALITY.DIGITAL_KEY_NOTES';
        view.notes = data.notes;
        const initialNotes = data.notes;
        this.translateHospitalityField(data.notes, value => {
          if (view.notes === initialNotes) {
            view.notes = value;
          }
        });
      }
      return view;
    }

    if (!data.notes) {
      return null;
    }

    const view: HospitalityDigitalKeyViewModel = {
      type,
      typeLabel,
      primaryLabelKey: 'LANDING.HOSPITALITY.DIGITAL_KEY_NOTES',
      primaryValue: data.notes,
    };
    const initialNotes = data.notes;
    this.translateHospitalityField(data.notes, value => {
      if (view.primaryValue === initialNotes) {
        view.primaryValue = value;
      }
    });
    if (data.link) {
      view.secondaryLinkLabelKey = 'LANDING.HOSPITALITY.DIGITAL_KEY_LINK';
      view.secondaryLink = data.link;
    }
    return view;
  }

  private getDigitalKeyTypeLabel(type: HospitalityDigitalKeyAccessType): string {
    switch (type) {
      case 'pin':
        return this.translate.instant('LANDING.HOSPITALITY.DIGITAL_KEY_TYPE_PIN');
      case 'app':
        return this.translate.instant('LANDING.HOSPITALITY.DIGITAL_KEY_TYPE_APP');
      default:
        return this.translate.instant('LANDING.HOSPITALITY.DIGITAL_KEY_TYPE_OTHER');
    }
  }

  private isHospitalitySectionPayload(value: any): value is { ownedEntries?: any[]; companyEntries?: any[] } {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const candidate = value as { ownedEntries?: any[]; companyEntries?: any[] };
    return Array.isArray(candidate.ownedEntries) || Array.isArray(candidate.companyEntries);
  }

  private resolveEntryOrder(order: any, fallback: number): number {
    if (typeof order === 'number' && Number.isFinite(order)) {
      return order;
    }
    const parsed = Number(order);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private filterAndSortEntryGroups<T extends { order?: number | null; isDeleted?: boolean }>(
    entries: Array<T | null | undefined>,
  ): T[] {
    if (!Array.isArray(entries)) {
      return [];
    }
    return entries
      .map((entry, index) => ({ entry, index }))
      .filter((item): item is { entry: T; index: number } => !!item.entry && !item.entry.isDeleted)
      .sort((a, b) => this.resolveEntryOrder(a.entry.order, a.index) - this.resolveEntryOrder(b.entry.order, b.index))
      .map(item => item.entry);
  }

  private mapCheckInOwnedEntryToTimeline(entry: any): HospitalityTimelineViewModel | null {
    if (!entry || typeof entry !== 'object') {
      return null;
    }
    const schedule = entry?.schedule && typeof entry.schedule === 'object' ? entry.schedule : {};
    const from = this.sanitizeString(schedule?.from);
    const until = this.sanitizeString(schedule?.until);
    const title = this.sanitizeString(entry?.title);
    const summary = this.sanitizeString(entry?.summary);
    const details = this.sanitizeString(entry?.details);
    const instructions = this.sanitizeString(entry?.instructions) || details || summary || title;
    const location =
      this.resolveValidMapAddress(entry?.keyPickupLocation)
      ?? this.resolveValidMapAddress(entry?.location);
    const contact = this.sanitizeString(entry?.contact);
    const links = this.normalizeHospitalityLinks(entry?.links);
    const digitalKeys = this.normalizeHospitalityDigitalKeys(entry?.digitalKeys);
    if (!(from || until || instructions || location || contact || links.length || digitalKeys.length)) {
      return null;
    }
    const view: HospitalityTimelineViewModel = {
      type: 'check-in',
      titleKey: 'LANDING.HOSPITALITY.CHECK_IN',
      from,
      until,
      instructions,
      location,
      contact,
      links,
      digitalKeys,
    };
    if (instructions) {
      this.translateHospitalityField(entry?.instructions ?? instructions, value => {
        if (view.instructions === instructions) {
          view.instructions = value;
        }
      });
    }
    if (contact) {
      this.translateHospitalityField(entry?.contact ?? contact, value => {
        if (view.contact === contact) {
          view.contact = value;
        }
      });
    }
    return view;
  }

  private mapCheckInCompanyEntryToTimeline(entry: any): HospitalityTimelineViewModel | null {
    if (!entry || typeof entry !== 'object') {
      return null;
    }
    const overrides = entry?.overrides && typeof entry.overrides === 'object' ? entry.overrides : {};
    const content = entry?.content && typeof entry.content === 'object' ? entry.content : {};
    const instructions = this.sanitizeString((overrides as any)?.instructions)
      || this.sanitizeString((overrides as any)?.details)
      || this.sanitizeString((overrides as any)?.summary)
      || this.sanitizeString((content as any)?.instructions)
      || this.sanitizeString((content as any)?.details)
      || this.sanitizeString((content as any)?.summary)
      || this.sanitizeString((content as any)?.title);
    const scheduleOverride = (overrides as any)?.schedule;
    const scheduleContent = (content as any)?.schedule;
    const from = this.sanitizeString(scheduleOverride?.from) || this.sanitizeString(scheduleContent?.from);
    const until = this.sanitizeString(scheduleOverride?.until) || this.sanitizeString(scheduleContent?.until);
    const location =
      this.resolveValidMapAddress((overrides as any)?.keyPickupLocation)
      ?? this.resolveValidMapAddress((overrides as any)?.location)
      ?? this.resolveValidMapAddress((content as any)?.keyPickupLocation)
      ?? this.resolveValidMapAddress((content as any)?.location);
    const contact = this.sanitizeString((overrides as any)?.contact) || this.sanitizeString((content as any)?.contact);
    const overrideLinks = this.normalizeHospitalityLinks((overrides as any)?.links, 'company');
    const contentLinks = this.normalizeHospitalityLinks((content as any)?.links, 'company');
    const overrideDigitalKeys = this.normalizeHospitalityDigitalKeys((overrides as any)?.digitalKeys, 'company');
    const contentDigitalKeys = this.normalizeHospitalityDigitalKeys((content as any)?.digitalKeys, 'company');
    if (!(instructions || from || until || location || contact || overrideLinks.length || contentLinks.length || overrideDigitalKeys.length || contentDigitalKeys.length)) {
      return null;
    }
    const view: HospitalityTimelineViewModel = {
      type: 'check-in',
      titleKey: 'LANDING.HOSPITALITY.CHECK_IN',
      from,
      until,
      instructions,
      location,
      contact,
      links: [...overrideLinks, ...contentLinks],
      digitalKeys: [...overrideDigitalKeys, ...contentDigitalKeys],
    };
    if (instructions) {
      this.translateHospitalityField((overrides as any)?.instructions ?? (content as any)?.instructions ?? instructions, value => {
        if (view.instructions === instructions) {
          view.instructions = value;
        }
      });
    }
    if (contact) {
      this.translateHospitalityField((overrides as any)?.contact ?? (content as any)?.contact ?? contact, value => {
        if (view.contact === contact) {
          view.contact = value;
        }
      });
    }
    return view;
  }

  private mapCheckOutOwnedEntryToTimeline(entry: any): HospitalityTimelineViewModel | null {
    if (!entry || typeof entry !== 'object') {
      return null;
    }
    const title = this.sanitizeString(entry?.title);
    const summary = this.sanitizeString(entry?.summary);
    const details = this.sanitizeString(entry?.details);
    const instructions = this.sanitizeString(entry?.instructions) || details || summary || title;
    const until = this.sanitizeString(entry?.checkOutTime) || this.sanitizeString(entry?.deadline);
    const location =
      this.resolveValidMapAddress(entry?.dropOffLocation)
      ?? this.resolveValidMapAddress(entry?.location);
    const contact = this.sanitizeString(entry?.contact);
    const links = this.normalizeHospitalityLinks(entry?.links);
    const checklistSource = entry?.checklist && typeof entry.checklist === 'object' ? entry.checklist : {};
    const checklistTitle = this.sanitizeString(checklistSource?.title);
    const checklistItems = this.normalizeStringArray(checklistSource?.items);
    const checklist = (checklistTitle || checklistItems.length)
      ? { title: checklistTitle || undefined, items: checklistItems }
      : undefined;
    if (!(until || instructions || location || contact || links.length || (checklist && checklist.items.length))) {
      return null;
    }
    const view: HospitalityTimelineViewModel = {
      type: 'check-out',
      titleKey: 'LANDING.HOSPITALITY.CHECK_OUT',
      from: undefined,
      until,
      instructions,
      location,
      contact,
      links,
      digitalKeys: [],
      checklist,
    };
    if (instructions) {
      this.translateHospitalityField(entry?.instructions ?? instructions, value => {
        if (view.instructions === instructions) {
          view.instructions = value;
        }
      });
    }
    if (contact) {
      this.translateHospitalityField(entry?.contact ?? contact, value => {
        if (view.contact === contact) {
          view.contact = value;
        }
      });
    }
    if (checklist) {
      if (checklist.title) {
        const initialTitle = checklist.title;
        this.translateHospitalityField(checklistSource?.title ?? checklistTitle, value => {
          if (view.checklist && view.checklist.title === initialTitle) {
            view.checklist.title = value;
          }
        });
      }
      checklistItems.forEach((item, index) => {
        const initialItem = item;
        this.translateHospitalityField(item, value => {
          if (view.checklist && view.checklist.items[index] === initialItem) {
            view.checklist.items[index] = value || initialItem;
          }
        });
      });
    }
    return view;
  }

  private mapCheckOutCompanyEntryToTimeline(entry: any): HospitalityTimelineViewModel | null {
    if (!entry || typeof entry !== 'object') {
      return null;
    }
    const overrides = entry?.overrides && typeof entry.overrides === 'object' ? entry.overrides : {};
    const content = entry?.content && typeof entry.content === 'object' ? entry.content : {};
    const instructions = this.sanitizeString((overrides as any)?.instructions)
      || this.sanitizeString((overrides as any)?.details)
      || this.sanitizeString((overrides as any)?.summary)
      || this.sanitizeString((content as any)?.instructions)
      || this.sanitizeString((content as any)?.details)
      || this.sanitizeString((content as any)?.summary)
      || this.sanitizeString((content as any)?.title);
    const until = this.sanitizeString((overrides as any)?.checkOutTime)
      || this.sanitizeString((content as any)?.checkOutTime)
      || this.sanitizeString((overrides as any)?.deadline)
      || this.sanitizeString((content as any)?.deadline);
    const location =
      this.resolveValidMapAddress((overrides as any)?.dropOffLocation)
      ?? this.resolveValidMapAddress((content as any)?.dropOffLocation)
      ?? this.resolveValidMapAddress((overrides as any)?.location)
      ?? this.resolveValidMapAddress((content as any)?.location);
    const contact = this.sanitizeString((overrides as any)?.contact) || this.sanitizeString((content as any)?.contact);
    const overrideLinks = this.normalizeHospitalityLinks((overrides as any)?.links, 'company');
    const contentLinks = this.normalizeHospitalityLinks((content as any)?.links, 'company');
    const overrideDigitalKeys = this.normalizeHospitalityDigitalKeys((overrides as any)?.digitalKeys, 'company');
    const contentDigitalKeys = this.normalizeHospitalityDigitalKeys((content as any)?.digitalKeys, 'company');
    const checklistSource = (overrides as any)?.checklist || (content as any)?.checklist;
    const checklistTitle = this.sanitizeString(checklistSource?.title);
    const checklistItems = this.normalizeStringArray(checklistSource?.items);
    const checklist = (checklistTitle || checklistItems.length)
      ? { title: checklistTitle || undefined, items: checklistItems }
      : undefined;
    if (!(until || instructions || location || contact || overrideLinks.length || contentLinks.length || overrideDigitalKeys.length || contentDigitalKeys.length || (checklist && checklist.items.length))) {
      return null;
    }
    const view: HospitalityTimelineViewModel = {
      type: 'check-out',
      titleKey: 'LANDING.HOSPITALITY.CHECK_OUT',
      from: undefined,
      until,
      instructions,
      location,
      contact,
      links: [...overrideLinks, ...contentLinks],
      digitalKeys: [...overrideDigitalKeys, ...contentDigitalKeys],
      checklist,
    };
    if (instructions) {
      this.translateHospitalityField((overrides as any)?.instructions ?? (content as any)?.instructions ?? instructions, value => {
        if (view.instructions === instructions) {
          view.instructions = value;
        }
      });
    }
    if (contact) {
      this.translateHospitalityField((overrides as any)?.contact ?? (content as any)?.contact ?? contact, value => {
        if (view.contact === contact) {
          view.contact = value;
        }
      });
    }
    if (checklist) {
      if (checklist.title) {
        const initialTitle = checklist.title;
        this.translateHospitalityField(checklistSource?.title ?? checklistTitle, value => {
          if (view.checklist && view.checklist.title === initialTitle) {
            view.checklist.title = value;
          }
        });
      }
      checklistItems.forEach((item, index) => {
        const initialItem = item;
        this.translateHospitalityField(item, value => {
          if (view.checklist && view.checklist.items[index] === initialItem) {
            view.checklist.items[index] = value || initialItem;
          }
        });
      });
    }
    return view;
  }

  private mapLegacyCheckInToTimeline(raw: any): HospitalityTimelineViewModel | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const from = this.sanitizeString(raw?.schedule?.from || raw?.from);
    const until = this.sanitizeString(raw?.schedule?.until || raw?.until);
    const instructions = this.sanitizeString(raw?.instructions)
      || this.sanitizeString(raw?.details)
      || this.sanitizeString(raw?.summary)
      || this.sanitizeString(raw?.title);
    const location = this.resolveValidMapAddress(raw?.location) || this.resolveValidMapAddress(raw?.keyPickupLocation);
    const contact = this.sanitizeString(raw?.contact);
    const links = this.normalizeHospitalityLinks(raw?.links);
    const digitalKeys = this.normalizeHospitalityDigitalKeys(raw?.digitalKeys);
    if (!(from || until || instructions || location || contact || links.length || digitalKeys.length)) {
      return null;
    }
    const view: HospitalityTimelineViewModel = {
      type: 'check-in',
      titleKey: 'LANDING.HOSPITALITY.CHECK_IN',
      from,
      until,
      instructions,
      location,
      contact,
      links,
      digitalKeys,
    };
    if (instructions) {
      this.translateHospitalityField(raw?.instructions ?? instructions, value => {
        if (view.instructions === instructions) {
          view.instructions = value;
        }
      });
    }
    if (contact) {
      this.translateHospitalityField(raw?.contact ?? contact, value => {
        if (view.contact === contact) {
          view.contact = value;
        }
      });
    }
    return view;
  }

  private mapLegacyCheckOutToTimeline(raw: any): HospitalityTimelineViewModel | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const until = this.sanitizeString(raw?.checkOutTime) || this.sanitizeString(raw?.deadline);
    const instructions = this.sanitizeString(raw?.instructions)
      || this.sanitizeString(raw?.details)
      || this.sanitizeString(raw?.summary)
      || this.sanitizeString(raw?.title);
    const location = this.resolveValidMapAddress(raw?.location) || this.resolveValidMapAddress(raw?.dropOffLocation);
    const contact = this.sanitizeString(raw?.contact);
    const links = this.normalizeHospitalityLinks(raw?.links);
    const checklistSource = raw?.checklist && typeof raw.checklist === 'object' ? raw.checklist : {};
    const checklistTitle = this.sanitizeString(checklistSource?.title);
    const checklistItems = this.normalizeStringArray(checklistSource?.items);
    const checklist = (checklistTitle || checklistItems.length)
      ? { title: checklistTitle || undefined, items: checklistItems }
      : undefined;
    if (!(until || instructions || location || contact || links.length || (checklist && checklist.items.length))) {
      return null;
    }
    const view: HospitalityTimelineViewModel = {
      type: 'check-out',
      titleKey: 'LANDING.HOSPITALITY.CHECK_OUT',
      from: undefined,
      until,
      instructions,
      location,
      contact,
      links,
      digitalKeys: [],
      checklist,
    };
    if (instructions) {
      this.translateHospitalityField(raw?.instructions ?? instructions, value => {
        if (view.instructions === instructions) {
          view.instructions = value;
        }
      });
    }
    if (contact) {
      this.translateHospitalityField(raw?.contact ?? contact, value => {
        if (view.contact === contact) {
          view.contact = value;
        }
      });
    }
    if (checklist) {
      if (checklist.title) {
        const initialTitle = checklist.title;
        this.translateHospitalityField(checklistSource?.title ?? checklistTitle, value => {
          if (view.checklist && view.checklist.title === initialTitle) {
            view.checklist.title = value;
          }
        });
      }
      checklistItems.forEach((item, index) => {
        const initialItem = item;
        this.translateHospitalityField(item, value => {
          if (view.checklist && view.checklist.items[index] === initialItem) {
            view.checklist.items[index] = value || initialItem;
          }
        });
      });
    }
    return view;
  }

  private createHospitalityFeedbackViewModel(raw: any, source: 'property' | 'company'): HospitalityFeedbackViewModel | undefined {
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }

    const instructions = this.sanitizeString(raw.instructions);
    const email = this.sanitizeString(raw.email);
    const phone = this.sanitizeString(raw.phone);
    const formUrlRaw = this.sanitizeString(raw.formUrl);
    const formUrl = formUrlRaw ? this.normalizeUrl(formUrlRaw) : undefined;

    if (instructions || email || phone || formUrl) {
      const view: HospitalityFeedbackViewModel = {
        instructions,
        email,
        phone,
        formUrl,
        source,
      };
      if (instructions) {
        this.translateHospitalityField(raw.instructions, value => {
          if (view.instructions === instructions) {
            view.instructions = value;
          }
        });
      }
      return view;
    }

    return undefined;
  }

  protected normalizeHospitalityRatingSummary(raw: any): HospitalityRatingSummary | undefined {
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }

    const parseAverage = (value: any) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? Math.round(numeric * 10) / 10 : 0;
    };

    const count = Number(raw.count);
    const normalizedCount = Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0;
    const averagesSource = raw.averages && typeof raw.averages === 'object' ? raw.averages : {};

    const averages = {
      cleanliness: parseAverage(averagesSource.cleanliness),
      comfort: parseAverage(averagesSource.comfort),
      communication: parseAverage(averagesSource.communication),
      overall: parseAverage(averagesSource.overall ?? raw.overallAverage),
    };

    const overallAverage = parseAverage(raw.overallAverage ?? averages.overall);
    const lastSubmittedAt = typeof raw.lastSubmittedAt === 'string' && raw.lastSubmittedAt
      ? raw.lastSubmittedAt
      : null;

    if (!normalizedCount && !overallAverage) {
      return undefined;
    }

    return {
      count: normalizedCount,
      averages,
      overallAverage,
      lastSubmittedAt,
    };
  }

  private extractHospitalityRatingSummary(details: any, landing: any): HospitalityRatingSummary | undefined {
    const propertySummary = this.normalizeHospitalityRatingSummary(details?.ratingSummary);
    if (propertySummary) {
      return propertySummary;
    }

    const companySummary = this.normalizeHospitalityRatingSummary(landing?.ratingSummary);
    if (companySummary) {
      return companySummary;
    }

    return undefined;
  }

  onHospitalityRatingChange(): void {
    if (this.hospitalityRatingSubmitAttempted) {
      this.hospitalityRatingError = undefined;
    }
  }

  submitHospitalityRating(): void {
    if (!this.companyId || !this.propertyId || this.hospitalityRatingSubmitting) {
      return;
    }

    this.hospitalityRatingSubmitAttempted = true;
    this.hospitalityRatingError = undefined;

    const missing = this.hospitalityRatingCategories.filter(({ key }) => !this.hospitalityRatingScores[key]);
    if (missing.length) {
      this.hospitalityRatingError = this.translate.instant('LANDING.HOSPITALITY.RATING_INCOMPLETE');
      return;
    }

    const payload = {
      cleanliness: this.hospitalityRatingScores.cleanliness,
      comfort: this.hospitalityRatingScores.comfort,
      communication: this.hospitalityRatingScores.communication,
      overall: this.hospitalityRatingScores.overall,
    };

    this.hospitalityRatingSubmitting = true;
    this.hospitalityRatingSubmitted = false;
    this.hospitalityRatingSuccess = undefined;

    this.ratingSubmissionSub?.unsubscribe();
    this.ratingSubmissionSub = this.propertyService
      .submitRating(this.companyId, this.propertyId, payload)
      .subscribe({
        next: (response) => {
          this.hospitalityRatingSubmitting = false;
          this.hospitalityRatingSubmitted = true;
          const summary = this.normalizeHospitalityRatingSummary(response?.summary);
          if (summary) {
            this.hospitalityRatingSummary = summary;
            this.showHospitalityEmptyState = false;
          }
          this.hospitalityRatingSuccess = this.translate.instant('LANDING.HOSPITALITY.RATING_SUCCESS');
        },
        error: (error) => {
          this.hospitalityRatingSubmitting = false;
          const status = error?.status;
          if (status === 429) {
            this.hospitalityRatingError = this.translate.instant('LANDING.HOSPITALITY.RATING_TOO_MANY');
          } else if (status === 400 && Array.isArray(error?.error?.errors) && error.error.errors.length) {
            this.hospitalityRatingError = error.error.errors.join(', ');
          } else if (typeof error?.error?.message === 'string') {
            this.hospitalityRatingError = error.error.message;
          } else {
            this.hospitalityRatingError = this.translate.instant('LANDING.HOSPITALITY.RATING_ERROR');
          }
        }
      });
  }

  private normalizeGuideSteps(rawSteps: any, fallbackDetails?: string | null): string[] {
    if (Array.isArray(rawSteps)) {
      const normalized = this.normalizeStringArray(rawSteps);
      if (normalized.length) {
        return normalized;
      }
    }
    if (typeof fallbackDetails === 'string' && fallbackDetails.trim().length) {
      return fallbackDetails
        .split(/\r?\n/)
        .map(step => step.trim())
        .filter(step => step.length > 0);
    }
    if (typeof rawSteps === 'string' && rawSteps.trim().length) {
      return rawSteps
        .split(/\r?\n/)
        .map(step => step.trim())
        .filter(step => step.length > 0);
    }
    return [];
  }

  private normalizeStringArray(values: any): string[] {
    if (!Array.isArray(values)) {
      return [];
    }
    return values
      .map(value => typeof value === 'string' ? value.trim() : '')
      .filter(value => value.length > 0);
  }

  protected resolveValidMapAddress(value?: string | null): string | undefined {
    return toValidMapAddress(value);
  }

  protected sanitizeString(value: any): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }

  private setHospitalityLocalTips(
    value: HospitalityLocalTipsViewModel | undefined,
    propertyCoordinates?: MapCoordinates | null,
    propertyLabel?: string,
  ): void {
    if (value) {
      const markerData = this.computeLocalTipMarkers(
        value.highlights,
        propertyCoordinates ?? null,
        value.mapCoordinates || null,
        propertyLabel,
      );
      value.mapMarkers = markerData.markers;
      value.mapCenter = value.mapCenter ?? markerData.center;
      value.staticMapUrl = undefined;
      this.applyMapFallbacks(value);
    }
    this.hospitalityLocalTips = value;
    this.onLocalTipsUpdated(value);
  }

  // allow subclasses to react to local tips updates (e.g., render custom maps)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onLocalTipsUpdated(_value: HospitalityLocalTipsViewModel | undefined): void {
    // no-op by default
  }

  private computeLocalTipMarkers(
    highlights: HospitalityLinkViewModel[],
    propertyCoordinates: MapCoordinates | null,
    mapCoordinates: MapCoordinates | null,
    propertyLabel?: string,
  ): { markers: HospitalityMapMarker[]; center?: MapCoordinates } {
    const markers: HospitalityMapMarker[] = [];
    const seen = new Set<string>();

    const addMarker = (marker: HospitalityMapMarker) => {
      const key = `${marker.kind || 'point'}:${marker.lat.toFixed(6)}:${marker.lng.toFixed(6)}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      markers.push(marker);
    };

    if (propertyCoordinates) {
      addMarker({
        lat: propertyCoordinates.lat,
        lng: propertyCoordinates.lng,
        label: propertyLabel,
        kind: 'property',
      });
    }

    highlights.forEach(highlight => {
      const marker = this.mapHighlightToMarker(highlight);
      if (!marker) {
        return;
      }
      addMarker(marker);
    });

    const center =
      mapCoordinates ||
      propertyCoordinates ||
      (markers.length ? this.averageMarkerCoordinates(markers) : undefined);
    return { markers, center };
  }

  private mapHighlightToMarker(highlight: HospitalityLinkViewModel): HospitalityMapMarker | null {
    const coordinates = highlight.coordinates || highlight.mapLocation?.coordinates;
    if (!coordinates) {
      return null;
    }
    return {
      lat: coordinates.lat,
      lng: coordinates.lng,
      label: highlight.label,
      url: highlight.url,
      kind: 'highlight',
    };
  }

  private averageMarkerCoordinates(markers: HospitalityMapMarker[]): MapCoordinates {
    if (!markers.length) {
      return { lat: 0, lng: 0 };
    }
    const total = markers.reduce(
      (acc, marker) => {
        acc.lat += marker.lat;
        acc.lng += marker.lng;
        return acc;
      },
      { lat: 0, lng: 0 },
    );
    return {
      lat: total.lat / markers.length,
      lng: total.lng / markers.length,
    };
  }

  private applyMapFallbacks(value: HospitalityLocalTipsViewModel): void {
    if (!value.mapCenter) {
      return;
    }

    const fallback = this.createFallbackMapLinks(value.mapCenter, value.mapMarkers);

    if (!value.staticMapUrl && !value.mapEmbedUrl) {
      value.mapEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fallback.embedUrl);
    }

    if (!value.mapUrl) {
      value.mapUrl = fallback.mapUrl;
    }
  }

  private createFallbackMapLinks(
    center: MapCoordinates,
    markers: HospitalityMapMarker[],
  ): { embedUrl: string; mapUrl: string } {
    const zoom = this.estimateStaticMapZoom(markers, center) ?? 16;
    const lat = center.lat.toFixed(6);
    const lng = center.lng.toFixed(6);
    const delta = 0.01;
    const minLat = (center.lat - delta).toFixed(6);
    const maxLat = (center.lat + delta).toFixed(6);
    const minLng = (center.lng - delta).toFixed(6);
    const maxLng = (center.lng + delta).toFixed(6);
    const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;

    const embedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
    const mapUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=${zoom}/${lat}/${lng}`;

    return { embedUrl, mapUrl };
  }

  private estimateStaticMapZoom(markers: HospitalityMapMarker[], center?: MapCoordinates): number | undefined {
    const coordinates = markers.map(marker => marker as MapCoordinates);
    if (center) {
      coordinates.push(center);
    }
    if (!coordinates.length) {
      return undefined;
    }
    const lats = coordinates.map(point => point.lat);
    const lngs = coordinates.map(point => point.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff * Math.cos(((maxLat + minLat) / 2) * (Math.PI / 180)));

    if (maxDiff < 0.005) {
      return 16;
    }
    if (maxDiff < 0.01) {
      return 15;
    }
    if (maxDiff < 0.05) {
      return 14;
    }
    if (maxDiff < 0.1) {
      return 13;
    }
    if (maxDiff < 0.5) {
      return 12;
    }
    return 11;
  }

  private resolveMapCoordinates(value: any): MapCoordinates | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }
    const lat = toValidLatitude(value.lat ?? value.latitude);
    const lng = toValidLongitude(value.lng ?? value.lon ?? value.long ?? value.longitude);
    if (lat === undefined || lng === undefined) {
      return undefined;
    }
    return { lat, lng };
  }

  trackHospitalityLink(section: string, link: HospitalityLinkViewModel | HospitalityLink | null | undefined): void {
    const url = link?.url;
    if (!url) {
      return;
    }
    this.track('link-click', {
      context: 'hospitality',
      section,
      label: link.label,
      url,
    });
  }

  trackHospitalityExternal(section: string, label: string, url?: string): void {
    if (!url) {
      return;
    }
    const normalized = this.normalizeUrl(url);
    this.track('link-click', {
      context: 'hospitality',
      section,
      label,
      url: normalized,
    });
  }

  trackHospitalityFeedback(action: 'email' | 'phone' | 'form', value?: string): void {
    const metadata: any = {
      context: 'hospitality',
      section: 'feedback',
      action,
    };

    if (value) {
      metadata.value = value;
    }

    if (this.hospitalityFeedback?.source) {
      metadata.source = this.hospitalityFeedback.source;
    }

    this.track('hospitality-feedback', metadata);
  }

  copyToClipboard(value?: string, kind: 'wifi-ssid' | 'wifi-password' | 'generic' = 'generic'): void {
    if (!value) { return; }
    navigator.clipboard?.writeText(value)
      .then(() => {
        this.track('copy', { kind });
      })
      .catch(() => {
        const ta = document.createElement('textarea');
        ta.value = value;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); this.track('copy', { kind }); } catch {}
        document.body.removeChild(ta);
      });
  }

  getMapsLink(address: string): string | undefined {
    const normalized = this.resolveValidMapAddress(address);
    if (!normalized) {
      return undefined;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(normalized)}`;
  }

  openMaps(address: string): void {
    const link = this.getMapsLink(address);
    if (!link) {
      return;
    }
    window.open(link, '_blank', 'noopener');
  }

  protected normalizeUrl(url?: string): string {
    if (!url) return '';
    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
  }

  protected buildWhatsappLink(rawNumber?: string, includePrefilledMessage = true): string | undefined {
    const normalizedNumber = this.normalizeWhatsappNumber(rawNumber);
    if (!normalizedNumber) {
      return undefined;
    }
    const message = includePrefilledMessage ? this.getWhatsappMessage() : undefined;
    const query = message ? `?text=${encodeURIComponent(message)}` : '';
    return `https://wa.me/${normalizedNumber}${query}`;
  }

  private normalizeWhatsappNumber(rawNumber?: string): string | undefined {
    const sanitized = this.sanitizeString(rawNumber);
    if (!sanitized) {
      return undefined;
    }
    let normalized = sanitized.replace(/[^\d+]/g, '');
    if (!normalized) {
      return undefined;
    }
    if (normalized.startsWith('+')) {
      normalized = normalized.substring(1);
    }
    if (normalized.startsWith('00')) {
      normalized = normalized.substring(2);
    }
    normalized = normalized.replace(/\D/g, '');
    if (normalized.length < 6 || normalized.length > 15) {
      return undefined;
    }
    return normalized;
  }

  private getWhatsappMessage(): string | undefined {
    const context = this.sanitizeString(this.property?.address) || this.sanitizeString(this.company?.name);
    const key = context
      ? 'LANDING.HOSPITALITY.WHATSAPP_MESSAGE_WITH_CONTEXT'
      : 'LANDING.HOSPITALITY.WHATSAPP_MESSAGE';
    const translation = this.translate.instant(key, { context });
    if (!translation || translation === key) {
      return undefined;
    }
    return translation;
  }
}
